<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Registrar vacaciones históricas</h1>
    </div>

    <wz-card title="Datos del registro histórico">

      <!-- Empresa y colaborador -->
      <div class="flex flex-wrap -mx-2">
        <div class="w-full md:w-1/2 mb-3 px-2">
          <wz-select
            label="Empresa"
            required
            :items="companies"
            :value="form.companyId"
            defaultOption="Seleccione una empresa"
            :error="$v.form.companyId.$error && !$v.form.companyId.required ? 'Empresa es requerida' : ''"
            @change="onCompanyChange"
          />
        </div>
        <div class="w-full md:w-1/2 mb-3 px-2">
          <wz-select-search
            label="Colaborador"
            required
            :items="employees"
            :value="form.employeeId"
            :disabled="!form.companyId"
            defaultOption="Buscar por nombre o número de documento"
            :error="$v.form.employeeId.$error && !$v.form.employeeId.required ? 'Colaborador es requerido' : ''"
            @change="onEmployeeChange"
          />
        </div>
      </div>

      <wz-alert v-if="noActiveContractAndNoPeriods" variant="warning" icon="ri-information-line" class="mb-3">
        Este colaborador no tiene períodos vacacionales registrados.
      </wz-alert>

      <!-- Selección de período -->
      <div v-if="allPeriods.length > 0" class="mb-4">
        <label class="wz-form-label mb-2">
          Período vacacional <span class="wz-form-required">*</span>
        </label>
        <div class="overflow-x-auto">
          <table class="wz-table w-full px-2">
            <thead>
              <tr>
                <th></th>
                <th>Inicio período</th>
                <th>Fin período</th>
                <th>Días ganados</th>
                <th>Días tomados</th>
                <th>Días disponibles</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="period in allPeriods"
                :key="period.id"
                :class="{ 'wz-tr--selected': form.vacationPeriodId === period.id }"
                style="cursor: pointer"
                @click="selectPeriod(period)"
              >
                <td>
                  <input type="radio" :value="period.id" v-model="form.vacationPeriodId" />
                </td>
                <td v-local-date="period.startDate"></td>
                <td v-local-date="period.endDate"></td>
                <td>{{ period.daysEarned }}</td>
                <td>{{ period.daysTaken }}</td>
                <td><strong>{{ period.daysPending }}</strong></td>
                <td>
                  <wz-badge :variant="getPeriodStatusColor(period.status)">{{ period.status }}</wz-badge>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <span v-if="$v.form.vacationPeriodId.$error" class="wz-form-error-msg mt-1">
          <i class="ri-error-warning-line"></i>Debe seleccionar un período
        </span>
      </div>

      <!-- Fechas (sin restricción de fecha mínima) -->
      <div v-if="form.vacationPeriodId" class="flex flex-wrap mb-3 -mx-2">
        <div class="w-full md:w-1/3 mb-3 px-2">
          <wz-datepicker
            label="Fecha de inicio"
            required
            v-model="form.startDate"
            :error="$v.form.startDate.$error && !$v.form.startDate.required ? 'La fecha de inicio es obligatoria' : ''"
            @change="calculateDays"
          />
        </div>
        <div class="w-full md:w-1/3 mb-3 px-2">
          <wz-datepicker
            label="Fecha de fin"
            required
            v-model="form.endDate"
            :error="$v.form.endDate.$error && !$v.form.endDate.required ? 'La fecha de fin es obligatoria' : ''"
            @change="calculateDays"
          />
        </div>
        <div class="w-full md:w-1/3 flex items-center px-2">
          <div v-if="calendarDays !== null" class="md:mt-3">
            <wz-badge :variant="daysExceedBalance ? 'danger-solid' : 'success-solid'">
              {{ calendarDays }} días calendario
            </wz-badge>
            <div v-if="daysExceedBalance" class="text-danger text-sm mt-1">
              Excede los días disponibles del período ({{ selectedPeriod ? selectedPeriod.daysPending : 0 }})
            </div>
          </div>
        </div>
      </div>

      <!-- Toggles: gozada / pagada -->
      <div v-if="form.vacationPeriodId" class="flex flex-wrap mb-2 -mx-2">
        <div class="w-full md:w-1/2 mb-3 px-2">
          <label class="wz-form-label mb-1">¿Esta vacación fue gozada?</label>
          <div class="flex items-center" style="gap:8px">
            <wz-checkbox v-model="form.enjoyed" name="enjoyed" switch size="lg" />
            <span class="text-sm font-bold">{{ form.enjoyed ? 'Sí' : 'No' }}</span>
          </div>
          <p class="text-muted text-sm mt-1 mb-0">
            {{ form.enjoyed ? 'Los días se descontarán del período' : 'Los días quedarán como pendientes de gozar' }}
          </p>
        </div>
        <div class="w-full md:w-1/2 mb-3 px-2">
          <label class="wz-form-label mb-1">¿Fue pagada?</label>
          <div class="flex items-center" style="gap:8px">
            <wz-checkbox v-model="form.paid" name="paid" switch size="lg" />
            <span class="text-sm font-bold">{{ form.paid ? 'Sí' : 'No' }}</span>
          </div>
        </div>
      </div>

      <!-- Datos de pago (todos opcionales para históricos) -->
      <div v-if="form.vacationPeriodId && form.paid" class="flex flex-wrap mb-3 -mx-2">
        <div class="w-full md:w-1/3 mb-3 px-2">
          <wz-datepicker label="Fecha de pago" v-model="form.paidAt" />
        </div>
        <div class="w-full md:w-1/3 mb-3 px-2">
          <wz-select
            label="Banco"
            :items="bankOptions"
            :value="form.bankId"
            defaultOption="Seleccione un banco"
            @change="form.bankId = $event"
          />
        </div>
        <div class="w-full md:w-1/3 mb-3 px-2">
          <wz-input
            label="Número de operación"
            v-model="form.operationNumber"
            maxlength="50"
            placeholder="N° de operación (opcional)"
          />
        </div>
      </div>

      <!-- Observaciones -->
      <div v-if="form.vacationPeriodId" class="flex flex-wrap mb-3 -mx-2">
        <div class="w-full px-2">
          <wz-textarea
            label="Observaciones"
            v-model="form.observations"
            placeholder="Ej: Vacaciones tomadas en enero 2022 sin registro previo"
            :rows="3"
          />
        </div>
      </div>

      <!-- Botones -->
      <div class="flex justify-end gap-2 mt-2">
        <wz-button variant="light" @click="$router.push({ name: 'vacation-requests-list' })">
          Cancelar
        </wz-button>
        <wz-button
          variant="primary"
          class="ml-2"
          :disabled="!canSave"
          @click="save()"
        >
          Registrar
        </wz-button>
      </div>

    </wz-card>
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { required } from 'vuelidate/lib/validators'
import { VacationService, EmployeeService, CompanyService } from '@/core/services'
import { Constants } from '@/core/utils'

export default {
  name: 'VacationHistoricalCreate',
  data() {
    return {
      companies: [],
      employees: [],
      banks: [],
      allPeriods: [],
      noActiveContractAndNoPeriods: false,
      calendarDays: null,
      form: {
        companyId:        null,
        employeeId:       null,
        contractId:       null,
        vacationPeriodId: null,
        startDate:        null,
        endDate:          null,
        observations:     '',
        enjoyed:          true,
        paid:             false,
        paidAt:           null,
        bankId:           null,
        operationNumber:  '',
      },
    }
  },
  validations: {
    form: {
      companyId:        { required },
      employeeId:       { required },
      vacationPeriodId: { required },
      startDate:        { required },
      endDate:          { required },
    },
  },
  computed: {
    selectedPeriod() {
      if (!this.form.vacationPeriodId) return null
      return this.allPeriods.find(p => p.id === this.form.vacationPeriodId) || null
    },
    bankOptions() {
      return this.banks.map(b => ({ key: b.id, label: b.name }))
    },
    daysExceedBalance() {
      // Si no fue gozada, no descuenta días → no se valida saldo
      if (!this.form.enjoyed) return false
      if (!this.selectedPeriod || this.calendarDays === null) return false
      return this.calendarDays > this.selectedPeriod.daysPending
    },
    canSave() {
      return (
        this.form.companyId &&
        this.form.employeeId &&
        this.form.contractId &&
        this.form.vacationPeriodId &&
        this.form.startDate &&
        this.form.endDate &&
        this.calendarDays > 0 &&
        !this.daysExceedBalance
      )
    },
  },
  methods: {
    async onCompanyChange(companyId) {
      this.form.companyId = companyId
      this.employees = []
      this.resetEmployeeSelection()
      if (!companyId) return
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await EmployeeService.listByCompany(companyId)
        const seen = new Set()
        this.employees = (resp.payload || [])
          .filter(e => e && e.id != null && !seen.has(e.id) && seen.add(e.id))
          .map(e => ({ key: e.id, label: e.description ?? '' }))
      } catch (e) {
        Alert.error('Error al cargar colaboradores de la empresa')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    resetEmployeeSelection() {
      this.form.employeeId = null
      this.form.contractId = null
      this.allPeriods = []
      this.noActiveContractAndNoPeriods = false
      this.form.vacationPeriodId = null
      this.form.startDate = null
      this.form.endDate = null
      this.calendarDays = null
    },
    async onEmployeeChange(employeeId) {
      this.resetEmployeeSelection()
      this.form.employeeId = employeeId
      if (!employeeId) return
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await VacationService.getBalanceByEmployeeCompany(employeeId, this.form.companyId)
        const balance = resp.payload
        if (balance && balance.contractId) {
          this.form.contractId = balance.contractId
        }
        if (balance && balance.periods && balance.periods.length > 0) {
          // Excluye CANCELADO (historia congelada por cambio de fecha de ingreso): no seleccionable.
          this.allPeriods = balance.periods.filter(p => p.status !== 'CANCELADO')
        } else {
          this.noActiveContractAndNoPeriods = true
        }
      } catch (e) {
        this.noActiveContractAndNoPeriods = true
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    selectPeriod(period) {
      this.form.vacationPeriodId = period.id
      this.form.startDate = null
      this.form.endDate = null
      this.calendarDays = null
    },
    calculateDays() {
      if (!this.form.startDate || !this.form.endDate) {
        this.calendarDays = null
        return
      }
      const start = new Date(this.form.startDate)
      const end   = new Date(this.form.endDate)
      const diff  = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1
      this.calendarDays = diff > 0 ? diff : null
    },
    getPeriodStatusColor(status) {
      const found = Object.values(Constants.VacationPeriodStatus).find(s => s.key === status)
      return found ? found.color : 'wz-badge--secondary'
    },
    async save() {
      this.$v.$touch()
      if (this.$v.$error || !this.canSave) return
      try {
        this.$store.dispatch('app/loading', true)
        await VacationService.createHistoricalRequest({
          contractId:       this.form.contractId,
          vacationPeriodId: this.form.vacationPeriodId,
          startDate:        this.form.startDate,
          endDate:          this.form.endDate,
          observations:     this.form.observations || null,
          enjoyed:          this.form.enjoyed,
          paid:             this.form.paid,
          paidAt:           this.form.paid ? (this.form.paidAt || null) : null,
          bankId:           this.form.paid ? (this.form.bankId || null) : null,
          operationNumber:  this.form.paid ? (this.form.operationNumber || null) : null,
        })
        Alert.success('Vacaciones históricas registradas correctamente')
        this.$router.push({ name: 'vacation-requests-list' })
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al registrar las vacaciones históricas')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
  },
  async created() {
    try {
      const [companiesResp, banksResp] = await Promise.all([
        CompanyService.listAll(),
        VacationService.getBanks().catch(() => ({ payload: [] })),
      ])
      this.companies = (companiesResp.payload || []).map(c => ({ key: c.id, label: c.name }))
      this.banks = banksResp.payload || []
    } catch (e) {
      console.error(e)
    }
    // Prefill opcional desde query (?companyId=&employeeId=)
    if (this.$route.query.companyId) {
      await this.onCompanyChange(Number(this.$route.query.companyId))
      if (this.$route.query.employeeId) {
        this.onEmployeeChange(Number(this.$route.query.employeeId))
      }
    }
  },
}
</script>

<style lang="stylus" scoped>
.wz-tr--selected
  background-color: var(--admin-primary-light, rgba(99, 102, 241, 0.06))
</style>
